import { useState, useMemo, useEffect, useRef, Fragment } from "react";
import { dismissModal } from "@/utils/modalClose";
import { TOAST_CLOSE_BUTTONS } from "@/utils/toast";
import { IonDateInput } from "@/components/DateInput";
import { FLOAT } from "@/components/SettingsRows";
import { deliverDownload } from "@/utils/pendingDownload";
import { emitAppLive, APP_LIVE_EVENT } from "@/utils/appLive";
import { taxRateDefaultsFor } from "@/utils/taxRateDefaults";
import { bizDraftKey } from "@/utils/bizDraft";
import { addReadyNotification } from "@/utils/appNotifications";
import UploadDropzone, { UploadedImage } from "@/components/UploadDropzone";
import { SavedLogoSelect, useSavedAssets } from "@/components/SavedAssets";
import SelectExistingEmployee from "@/components/SelectExistingEmployee";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import AppSeo from "@/components/AppSeo";
import { useGeneratorGate } from "@/utils/generatorAvailability";
import {
  IonHeader, IonToolbar, IonTitle, IonButtons,
  IonInput, IonSelect, IonSelectOption,
  IonList, IonItem, IonLabel, IonButton, IonIcon, IonGrid, IonRow, IonCol,
  IonNote, IonSpinner, IonSegment, IonSegmentButton, IonToggle, IonAlert,
  IonBadge, IonToast, IonSkeletonText,
} from "@ionic/react";
import { trashOutline, addOutline, chevronDownOutline, chevronForwardOutline, cloudDownloadOutline, eyeOutline, closeOutline, checkmarkOutline, pricetagOutline, arrowBackOutline, personOutline, briefcaseOutline } from "ionicons/icons";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { generateAndDownloadUkPayslip } from "@/utils/ukPayslipGenerator";
import CheckoutWithPaywall from "@/components/CheckoutWithPaywall";
import { useBundlePrice } from "@/utils/bundles";
import { useDocPrice } from "@/utils/docPrices";
import PreviewPager from "@/components/PreviewPager";
import { generateAllUkPreviewImages } from "@/utils/ukPayslipPreviewGenerator";
import { fetchPublishedLayout, fetchCustomLayout, isCustomTemplate } from "@/utils/layoutEngine";
import { userHeaders, isUserLoggedIn } from "@/utils/userAuth";
import { isNative, nativePost, getStripeOrigin } from "@/utils/nativeHttp"; // eslint-disable-line no-unused-vars
import { confirmAlert } from "@/utils/confirmAlert";
import { flagEmptyRequiredFields } from "@/utils/requiredFields";
import {
  NI_CATEGORIES, STUDENT_LOAN_PLANS,
  formatNiNumber, validateNiNumber,
  formatPostcode, validatePostcode,
} from "@/utils/ukTaxRates";
import { UK_PAY_FREQUENCIES, periodEnd as ukPeriodEnd, countPeriods as ukCountPeriods, defaultHours as ukDefaultHours } from "@/utils/ukPayPeriods";
import { UK_PAYMENT_METHODS } from "@/utils/ukPaymentMethods";
import {
  formatPhoneNumber, validatePhoneNumber,
  formatBankLast4, validateBankLast4,
} from "@/utils/validation";
import OnPayLogo from "../../assests/onpayLogo.webp";

import FreshOverlay from "@/components/FreshOverlay";
import { hasFreeDownloads, freeDownloadCount, walletBalance, planIsLive } from "@/utils/freeDownloads";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Date-only strings must parse as LOCAL dates — new Date("YYYY-MM-DD") is UTC
// midnight, which reads back as the previous day in North American timezones.
const parseLocalDate = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const cardStyle = { backgroundColor: "var(--ion-card-background)", borderRadius: 8, boxShadow: "rgba(0,0,0,0.18) 0px 4px 24px", padding: 16, display: "flex", flexDirection: "column", gap: 16 };
const sectionHeadingStyle = { fontWeight: 700, fontSize: "0.95rem", color: "var(--ion-text-color)", display: "flex", alignItems: "center", gap: 8 };


// Sample data used to render the template card previews
const UK_PREVIEW_SAMPLE_DATA = {
  name: "Emma Clarke", niNumber: "AB 12 34 56 C", bank: "4821", bankName: "Barclays",
  address: "14 Victoria Road", city: "Manchester", postcode: "M1 4BT",
  company: "Northgate Consulting Ltd", companyAddress: "22 Deansgate",
  companyCity: "Manchester", companyPostcode: "M3 1AB",
  companyPhone: "0161 496 0123",
  hireDate: "2022-01-04", startDate: "2025-01-01", endDate: "2025-01-31",
  rate: "18.50", payFrequency: "monthly", payDay: "Friday",
  payType: "hourly", workerType: "employee", annualSalary: "",
  employeeId: "EMP001", companyCode: "", locDept: "", checkNumber: "",
  taxCode: "1257L", niCategory: "A", studentLoanPlan: "none", paymentMethod: "bacs",
  hoursList: "162.5", overtimeList: "0", commissionList: "0",
  startDateList: "2025-01-01", endDateList: "2025-01-31", payDateList: "2025-01-31",
};

const PAYROLL_COMPANIES = [
  { id: "onpay", name: "OnPay", template: "template-h", logo: OnPayLogo, color: "#0f172a" },
  { id: "sage", name: "Sage", template: "template-s", logo: null, color: "#008a3e" },
];

const STORAGE_KEY = "ukPayslipFormData";
const RATE_COUNTRY = "uk";
const RATE_FIELDS = [["payeRate", "paye"], ["niRate", "ni"]];

const defaultFormData = {
  name: "", niNumber: "", bank: "", bankName: "",
  address: "", city: "", postcode: "",
  company: "", companyAddress: "", companyCity: "",
  companyPostcode: "", companyPhone: "",
  hireDate: "", startDate: "", endDate: "",
  rate: "", payFrequency: "monthly", payDay: "Friday",
  hoursList: "", overtimeList: "", commissionList: "",
  startDateList: "", endDateList: "", payDateList: "",
  workerType: "employee", payType: "hourly", annualSalary: "",
  employeeId: "", companyCode: "", locDept: "", checkNumber: "",
  taxCode: "1257L", niCategory: "A", studentLoanPlan: "none", paymentMethod: "bacs",
  // Tax Settings rates; blank means the automatic PAYE band and NI threshold maths
  payeRate: "", niRate: "",
  accentColor: "",
};

export const deductionTypes = [
  { label: "Private Medical",     value: "private_medical", preTax: false },
  { label: "Union Subscription",  value: "union_dues",      preTax: false },
  { label: "Season Ticket Loan",  value: "season_ticket",   preTax: false },
  { label: "Childcare Vouchers",  value: "childcare",       preTax: true },
  { label: "Cycle to Work",       value: "cycle_to_work",   preTax: true },
  { label: "Parking",             value: "parking",         preTax: false },
  { label: "Other",               value: "other",           preTax: false },
];

export const contributionTypes = [
  { label: "Workplace Pension (Pre-Tax)", value: "pension",          preTax: true },
  { label: "Salary Sacrifice Pension",    value: "salary_sacrifice", preTax: true },
  { label: "AVC (Additional Pension)",    value: "avc",              preTax: true },
  { label: "Share Save Scheme",           value: "sharesave",        preTax: false },
  { label: "Other",                       value: "other",            preTax: false },
];

export const employerBenefitTypes = [
  { label: "Employer Pension Contribution", value: "pension_match" },
  { label: "Private Medical Insurance",     value: "health_insurance" },
  { label: "Life Assurance",                value: "life_insurance" },
  { label: "Employer Paid Dental",          value: "dental" },
  { label: "Income Protection",             value: "disability" },
  { label: "Other Employer Benefit",        value: "other" },
];

export default function AppUkPayslip({ embedTemplate = null, onEmbedClose = null } = {}) {
  // Embedded mode: the form modal renders inside a business's own category
  // (no page chrome, no landing cards), preselected on their design.
  const embed = !!embedTemplate;
  // A business's own design is part of its plan: never priced, never charged
  // to the wallet (the server still counts the download against the plan).
  const ownDesign = embed && String(embedTemplate).startsWith("usercustom:");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateFromUrl = searchParams.get("template");
  // The page itself is gone when the generator is hidden from this visitor
  // (never from an embedded form)
  useGeneratorGate(embed ? [] : ["uk-payslip"]);

  // ── Auth / subscription ──────────────────────────────────────────────────
  const [user,                  setUser]                  = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isProcessing,          setIsProcessing]          = useState(false);
  const [appliedDiscount,       setAppliedDiscount]       = useState(null); // eslint-disable-line

  // ── Template ─────────────────────────────────────────────────────────────
  const isKnownTemplate = (v) =>
    ["template-h", "template-s"].includes(v) || isCustomTemplate(v);

  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    if (embed) return embedTemplate;
    if (templateFromUrl && isKnownTemplate(templateFromUrl)) return templateFromUrl;
    try {
      const saved = localStorage.getItem("ukPayslipTemplate");
      if (saved && isKnownTemplate(saved)) return saved;
    } catch {}
    return "template-h";
  });

  useEffect(() => {
    if (embed) return;  // an embedded form must not overwrite the user's normal choice
    try { localStorage.setItem("ukPayslipTemplate", selectedTemplate); } catch {}
  }, [selectedTemplate, embed]);

  // Customer color choice, when the selected custom template offers one
  const [accentOption, setAccentOption] = useState(null);
  const [customWorkerTypes, setCustomWorkerTypes] = useState("both"); // a custom template can allow W-2, contractor, or both
  useEffect(() => {
    let cancelled = false;
    if (!isCustomTemplate(selectedTemplate)) {
      setAccentOption(null);
      setCustomWorkerTypes("both");
      setFormData((prev) => (prev.accentColor ? { ...prev, accentColor: "" } : prev));
      return;
    }
    fetchCustomLayout(selectedTemplate).then((layout) => {
      if (cancelled) return;
      const wt = layout?.meta?.workerTypes;
      setCustomWorkerTypes(wt === "employee" || wt === "contractor" ? wt : "both");
      const opt = layout?.accentOption;
      setAccentOption(opt && opt.enabled ? opt : null);
      setFormData((prev) => {
        if (!prev.accentColor) return prev;
        const ok = opt && opt.enabled && (opt.swatches || []).includes(prev.accentColor);
        return ok ? prev : { ...prev, accentColor: "" };
      });
    });
    return () => { cancelled = true; };
  }, [selectedTemplate]);

  // ── Company logo ─────────────────────────────────────────────────────────
  const [companyLogo,           setCompanyLogo]           = useState(null);
  const [logoPreview,           setLogoPreview]           = useState(null);
  const [isDragging,            setIsDragging]            = useState(false);
  const [logoError,             setLogoError]             = useState("");
  const logoInputRef     = useRef(null);

  // ── Per-period state ──────────────────────────────────────────────────────
  const [hoursExpanded,   setHoursExpanded]   = useState(false);
  const formRef = useRef(null); // the cards, for the required-field gate before a preview
  const [moreOpen,        setMoreOpen]        = useState(false); // the optional sections under Additional options
  const [hoursPerPeriod,  setHoursPerPeriod]  = useState([]);
  const [deductions,      setDeductions]      = useState([]);
  const [contributions,   setContributions]   = useState([]);
  const [absencePlans,    setAbsencePlans]    = useState([]);
  const [employerBenefits,setEmployerBenefits]= useState([]);

  // ── Form data ────────────────────────────────────────────────────────────
  // Drafts are per-business: operating another business must not surface
  // this one's employee or company data.
  const [draftKey, setDraftKey] = useState(() => bizDraftKey(STORAGE_KEY));
  const loadDraft = (key) => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) return { ...defaultFormData, ...JSON.parse(saved) };
    } catch {}
    return defaultFormData;
  };
  const [formData, setFormData] = useState(() => loadDraft(bizDraftKey(STORAGE_KEY)));

  useEffect(() => {
    try { localStorage.setItem(draftKey, JSON.stringify(formData)); } catch {}
  }, [formData, draftKey]);


  // ── Tax rate defaults ──
  // The Tax Settings rate inputs seed from the defaults for the stub's
  // year (admin-set per year, built-ins otherwise). First load fills only
  // fields a draft does not already carry; a change of year re-seeds them.
  const rateYear = String((parseInt(String(formData.endDate || "").slice(0, 4), 10) || new Date().getFullYear()));
  const rateSeedRef = useRef(null);
  useEffect(() => {
    let on = true;
    taxRateDefaultsFor(RATE_COUNTRY, rateYear).then((d) => {
      if (!on) return;
      const firstRun = rateSeedRef.current === null;
      const yearChanged = rateSeedRef.current !== null && rateSeedRef.current !== rateYear;
      rateSeedRef.current = rateYear;
      setFormData((prev) => {
        const next = { ...prev };
        for (const [field, key] of RATE_FIELDS) {
          // a field the user has not touched (still the built-in default)
          // follows the year's defaults; an edited one is left alone
          const untouched = prev[field] === undefined || prev[field] === defaultFormData[field];
          if (yearChanged || (firstRun && untouched)) next[field] = d[key];
        }
        return next;
      });
    });
    return () => { on = false; };
  }, [rateYear]); // eslint-disable-line

  // Switching businesses while this form is open swaps in that business's
  // own draft (the one being left was already persisted above).
  useEffect(() => {
    const onLive = () => {
      const k = bizDraftKey(STORAGE_KEY);
      if (k !== draftKey) { setDraftKey(k); setFormData(loadDraft(k)); }
    };
    window.addEventListener(APP_LIVE_EVENT, onLive);
    return () => window.removeEventListener(APP_LIVE_EVENT, onLive);
  }, [draftKey]); // eslint-disable-line

  // ── Validation errors ────────────────────────────────────────────────────
  const [validationErrors, setValidationErrors] = useState({
    niNumber: "", bank: "", postcode: "", companyPostcode: "", companyPhone: "",
    companyCode: "", locDept: "", checkNumber: "",
  });

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toastState, setToastState] = useState({ isOpen: false, message: "", color: "danger" });
  const showToast = (message, color = "danger") => setToastState({ isOpen: true, message, color });

  const formatCurrency = (num) =>
    Number(num).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Subscription check ────────────────────────────────────────────────────
  useEffect(() => {
    checkUserSubscription();
    const f = () => checkUserSubscription();
    window.addEventListener("ms:entitlements-changed", f);
    return () => window.removeEventListener("ms:entitlements-changed", f);
  }, []);

  const checkUserSubscription = async () => {
    const token    = localStorage.getItem("userToken");
    const userInfo = localStorage.getItem("userInfo");
    if (token && userInfo) {
      try {
        const userData = JSON.parse(userInfo);
        setUser(userData);
        if (hasFreeDownloads(userData)) {
          setHasActiveSubscription(true);
        }
        const res = await fetch(`${BACKEND_URL}/api/user/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.user) {
            setUser(data.user);
            localStorage.setItem("userInfo", JSON.stringify(data.user));
            setHasActiveSubscription(hasFreeDownloads(data.user));
          }
        }
      } catch (err) { console.error("Error checking subscription:", err); }
    }
  };

  // ── Subscription download ─────────────────────────────────────────────────
  // Rides the same /payment-success animation page a purchase does.
  const handleSubscriptionDownload = () => {
    const token = localStorage.getItem("userToken");
    if (!token && !isFree) { showToast("Please log in to use your subscription"); navigate("/login"); return; }
    const fullFormData = {
      ...formData, deductions, contributions, absencePlans, employerBenefits,
      companyLogo, logoDataUrl: logoPreview,
    };
    localStorage.setItem("pendingUkPayslipData", JSON.stringify(fullFormData));
    localStorage.setItem("pendingUkPayslipTemplate", selectedTemplate);
    localStorage.setItem("pendingUkPayslipCount", calculateNumStubs.toString());
    localStorage.setItem("pendingSubTemplate", selectedTemplate);
    localStorage.setItem("pendingSubAmount", String(ownDesign ? 0 : (stubPricing.price || 0))); // the wallet pays this exact price
    localStorage.removeItem("pendingCustomerEmail");
    localStorage.setItem("pendingResetKeys", JSON.stringify([draftKey, "ukPayslipTemplate", "ukPayslipCompanyLogo"]));
    navigate(`/payment-success?type=uk-payslip&count=${calculateNumStubs}&source=app&${isFree ? "free=1" : "sub=1"}`);
  };

  // ── handleChange ──────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  // ── Validated handlers ────────────────────────────────────────────────────
  const handleNiNumberChange = (val) => {
    const formatted = formatNiNumber(val);
    setFormData(prev => ({ ...prev, niNumber: formatted }));
    setValidationErrors(prev => ({ ...prev, niNumber: validateNiNumber(formatted).error || "" }));
  };
  const handleBankChange = (val) => {
    const formatted = formatBankLast4(val);
    setFormData(prev => ({ ...prev, bank: formatted }));
    setValidationErrors(prev => ({ ...prev, bank: validateBankLast4(formatted).error || "" }));
  };
  const handlePostcodeChange = (val) => {
    const formatted = formatPostcode(val);
    setFormData(prev => ({ ...prev, postcode: formatted }));
    setValidationErrors(prev => ({ ...prev, postcode: validatePostcode(formatted).error || "" }));
  };
  const handleCompanyPostcodeChange = (val) => {
    const formatted = formatPostcode(val);
    setFormData(prev => ({ ...prev, companyPostcode: formatted }));
    setValidationErrors(prev => ({ ...prev, companyPostcode: validatePostcode(formatted).error || "" }));
  };
  const handleCompanyPhoneChange = (val) => {
    const formatted = formatPhoneNumber(val);
    setFormData(prev => ({ ...prev, companyPhone: formatted }));
    setValidationErrors(prev => ({ ...prev, companyPhone: validatePhoneNumber(formatted).error || "" }));
  };
  const handleCompanyCodeChange = (val) => {
    const filtered = val.toUpperCase().replace(/[^A-Z0-9\s/]/g, "").slice(0, 20);
    setFormData(prev => ({ ...prev, companyCode: filtered }));
    setValidationErrors(prev => ({ ...prev, companyCode: filtered && filtered.length < 3 ? "Min 3 characters" : "" }));
  };
  const handleLocDeptChange = (val) => {
    const filtered = val.replace(/[^0-9]/g, "").slice(0, 3);
    setFormData(prev => ({ ...prev, locDept: filtered }));
    setValidationErrors(prev => ({ ...prev, locDept: filtered && filtered.length !== 3 ? "Must be 3 digits" : "" }));
  };
  const handleCheckNumberChange = (val) => {
    const filtered = val.replace(/[^0-9]/g, "").slice(0, 7);
    setFormData(prev => ({ ...prev, checkNumber: filtered }));
    setValidationErrors(prev => ({ ...prev, checkNumber: filtered && filtered.length < 6 ? "Must be 6-7 digits" : "" }));
  };

  // ── Worker / template changes ─────────────────────────────────────────────
  const canUseSalary = !(formData.workerType === "contractor" && selectedTemplate === "template-a");
  // Which worker types the selected template offers: custom templates declare
  // it; the Gusto/OnPay built-ins allow both; the rest are employee-only.
  const workerMode = isCustomTemplate(selectedTemplate)
    ? customWorkerTypes
    : (selectedTemplate === "template-a" || ["template-h", "template-s"].includes(selectedTemplate)) ? "both" : "employee";
  useEffect(() => {
    if (workerMode === "employee") setFormData((p) => (p.workerType === "employee" ? p : { ...p, workerType: "employee" }));
    else if (workerMode === "contractor") setFormData((p) => (p.workerType === "contractor" ? p : { ...p, workerType: "contractor" }));
  }, [workerMode]);

  const handleWorkerTypeChange = (val) => {
    setFormData(prev => {
      const newData = { ...prev, workerType: val };
      if (val === "contractor" && selectedTemplate === "template-a") newData.payType = "hourly";
      return newData;
    });
    if (val === "contractor") setHoursPerPeriod(prev => prev.map(p => ({ ...p, overtime: 0 })));
  };

  const handleTemplateChange = (val) => {
    setSelectedTemplate(val);
    setPdfPreviews([]); // the old template's images must not be served for the new one
    if (val === "template-c" && formData.workerType === "contractor") {
      setFormData(prev => ({ ...prev, workerType: "employee" }));
    }
    if (formData.workerType === "contractor" && val === "template-a") {
      setFormData(prev => ({ ...prev, payType: "hourly" }));
    }
  };

  // ── Logo handling ─────────────────────────────────────────────────────────
  const resizeImageToFit = (base64Data, maxWidth, maxHeight) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
        const canvas = document.createElement("canvas");
        canvas.width  = Math.round(img.width  * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => reject(new Error("Failed to load image for resizing"));
      img.src = base64Data;
    });

  const validateAndProcessLogo = async (file) => {
    setLogoError("");
    if (!file.type.includes("png") && !file.type.includes("jpeg") && !file.type.includes("jpg")) {
      setLogoError("Only PNG or JPG files are accepted"); return false;
    }
    if (file.size > 2 * 1024 * 1024) { setLogoError("File size must be under 2MB"); return false; }
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const resizedBase64 = await resizeImageToFit(e.target.result, 360, 105);
          localStorage.setItem("ukPayslipCompanyLogo", resizedBase64);
          setCompanyLogo(resizedBase64); setLogoPreview(resizedBase64);
          resolve(true);
        } catch { setLogoError("That logo could not be opened. Try a different file."); resolve(false); }
      };
      reader.onerror = () => { setLogoError("Could not read that file. Try a different one."); resolve(false); };
      reader.readAsDataURL(file);
    });
  };

  const handleLogoDrop  = async (e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files[0]) await validateAndProcessLogo(e.dataTransfer.files[0]); };
  const handleLogoSelect= async (e) => { if (e.target.files[0]) await validateAndProcessLogo(e.target.files[0]); };
  const { hasAccess: logoAccess, assets: logoAssets } = useSavedAssets("logo");
  const hasLogoSelect = logoAccess && logoAssets.length > 0; // then the select's label names the field
  const removeLogo = () => {
    setCompanyLogo(null); setLogoPreview(null);
    localStorage.removeItem("ukPayslipCompanyLogo");
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  useEffect(() => { localStorage.removeItem("ukPayslipCompanyLogo"); setCompanyLogo(null); setLogoPreview(null); }, []);
  useEffect(() => () => { localStorage.removeItem("ukPayslipCompanyLogo"); }, []);

  // ── calculateNumStubs ────────────────────────────────────────────────────
  const calculateNumStubs = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return 0;
    return ukCountPeriods(parseLocalDate(formData.startDate), parseLocalDate(formData.endDate), formData.payFrequency);
  }, [formData.startDate, formData.endDate, formData.payFrequency]);

  // ── payPeriods ───────────────────────────────────────────────────────────
  const payPeriods = useMemo(() => {
    if (!formData.startDate || !formData.endDate || calculateNumStubs === 0) return [];
    const periods = [];
    let currentStart = parseLocalDate(formData.startDate);
    const fmt = (date) => {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    };
    const getNextPayDay = (date, targetDay) => {
      const days = { Sunday:0,Monday:1,Tuesday:2,Wednesday:3,Thursday:4,Friday:5,Saturday:6 };
      const target = days[targetDay] ?? 5;
      const result = new Date(date);
      while (result.getDay() !== target) result.setDate(result.getDate() + 1);
      return result;
    };
    for (let i = 0; i < calculateNumStubs; i++) {
      const periodEnd = ukPeriodEnd(currentStart, formData.payFrequency);
      const payDate = getNextPayDay(periodEnd, formData.payDay);
      periods.push({
        index: i,
        start: fmt(currentStart), end: fmt(periodEnd), pay: fmt(payDate),
        label: `${currentStart.toLocaleDateString("en-GB",{month:"short",day:"numeric"})} - ${periodEnd.toLocaleDateString("en-GB",{month:"short",day:"numeric",year:"numeric"})}`,
      });
      currentStart = new Date(periodEnd);
      currentStart.setDate(currentStart.getDate() + 1);
    }
    return periods;
  }, [formData.startDate, formData.endDate, formData.payFrequency, formData.payDay, calculateNumStubs]);

  // ── Initialize hoursPerPeriod ────────────────────────────────────────────
  useEffect(() => {
    const defaultHours = ukDefaultHours(formData.payFrequency);
    if (payPeriods.length > 0) {
      setHoursPerPeriod(prev => {
        // When the period count changes (date range changed), reset dates to newly
        // computed values so stale cached dates from a previous session don't persist
        // and cause duplicate pay dates → ZIP filename collisions.
        const countChanged = prev.length !== payPeriods.length;
        return payPeriods.map((period, i) => ({
          hours:       prev[i]?.hours       ?? defaultHours,
          overtime:    prev[i]?.overtime    ?? 0,
          commission:  prev[i]?.commission  ?? 0,
          startDate:   (!countChanged && prev[i]?.startDate) ? prev[i].startDate : period.start,
          endDate:     (!countChanged && prev[i]?.endDate)   ? prev[i].endDate   : period.end,
          payDate:     (!countChanged && prev[i]?.payDate)   ? prev[i].payDate   : period.pay,
          checkNumber: prev[i]?.checkNumber ?? "",
          memo:        prev[i]?.memo        ?? "",
        }));
      });
    } else {
      setHoursPerPeriod([]);
    }
  }, [payPeriods.length, formData.payFrequency]);

  // ── Sync hoursPerPeriod → formData ───────────────────────────────────────
  useEffect(() => {
    if (hoursPerPeriod.length > 0) {
      setFormData(prev => ({
        ...prev,
        hoursList:     hoursPerPeriod.map(p => p.hours).join(", "),
        overtimeList:  hoursPerPeriod.map(p => p.overtime).join(", "),
        commissionList:hoursPerPeriod.map(p => p.commission).join(", "),
        startDateList: hoursPerPeriod.map(p => p.startDate || "").join(", "),
        endDateList:   hoursPerPeriod.map(p => p.endDate || "").join(", "),
        payDateList:   hoursPerPeriod.map(p => p.payDate || "").join(", "),
      }));
    }
  }, [hoursPerPeriod]);

  const handlePeriodHoursChange = (index, field, value) => {
    setHoursPerPeriod(prev => {
      const updated = [...prev];
      const stringFields = ["startDate","endDate","payDate","checkNumber","memo"];
      updated[index] = { ...updated[index], [field]: stringFields.includes(field) ? value : (parseFloat(value) || 0) };
      return updated;
    });
  };

  // ── Template card preview images ──────────────────────────────────────────
  const [templatePreviews, setTemplatePreviews] = useState({});
  const [loadingPreviews,  setLoadingPreviews]  = useState(true);

  // Admin-published custom templates (from the admin Doc Templates editor)
  const [customTemplates, setCustomTemplates] = useState([]);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/doc-templates?documentType=uk-payslip`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setCustomTemplates(d.templates || []); })
      .catch(() => {});
  }, []);

  // A Pro/Enterprise business's own published UK payslip designs are
  // selectable here too, so they run through this generator's full pay-data
  // form and tax computation rather than the free-form token form.
  const [userTemplates, setUserTemplates] = useState([]);
  useEffect(() => {
    if (!isUserLoggedIn()) return;
    fetch(`${BACKEND_URL}/api/user/doc-templates?documentType=uk-payslip&published=1`, { headers: userHeaders() })
      .then((r) => r.json())
      .then((d) => { if (d && d.success && d.hasAccess) setUserTemplates((d.templates || []).filter((t) => t.documentType === "uk-payslip")); })
      .catch(() => {});
  }, []);

  // Landing cards: built-in styles plus admin-published custom templates
  const templateCards = [
    ...PAYROLL_COMPANIES,
    ...customTemplates.map((t) => ({ id: `custom-${t.id}`, name: t.name, template: `custom:${t.id}`, color: t.badgeColor || "#34d399" })),
    ...userTemplates.map((t) => ({ id: `usercustom-${t.id}`, name: t.name, template: `usercustom:${t.id}`, color: t.badgeColor || "#34d399" })),
  ];

  useEffect(() => {
    Promise.all([
      generateAllUkPreviewImages(UK_PREVIEW_SAMPLE_DATA, "template-h", 1),
      generateAllUkPreviewImages(UK_PREVIEW_SAMPLE_DATA, "template-s", 1),
    ]).then(([h, s]) => {
      setTemplatePreviews((prev) => ({ ...prev, "template-h": h[0], "template-s": s[0] }));
      setLoadingPreviews(false);
    }).catch(() => setLoadingPreviews(false));
  }, []);

  // Previews for admin-published templates, merged in as each one renders
  useEffect(() => {
    userTemplates.forEach((t) => {
      const key = `usercustom:${t.id}`;
      generateAllUkPreviewImages(UK_PREVIEW_SAMPLE_DATA, key, 1)
        .then(([img]) => { if (img) setTemplatePreviews((prev) => ({ ...prev, [key]: img })); })
        .catch(() => {});
    });
    customTemplates.forEach((t) => {
      const key = `custom:${t.id}`;
      generateAllUkPreviewImages(UK_PREVIEW_SAMPLE_DATA, key, 1)
        .then(([img]) => { if (img) setTemplatePreviews((prev) => ({ ...prev, [key]: img })); })
        .catch(() => {});
    });
  }, [customTemplates, userTemplates]);

  // ── PDF preview state ─────────────────────────────────────────────────────
  const [pdfPreviews,         setPdfPreviews]         = useState([]);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [formModalOpen,       setFormModalOpen]       = useState(embed);
  const [previewModalOpen,    setPreviewModalOpen]    = useState(false);
  const [previewPageIndex,    setPreviewPageIndex]    = useState(0);
  const [pendingCheckout,     setPendingCheckout]     = useState(null); // {fullFormData} while the payment modal is open

  useEffect(() => {
    let cancelled = false; // a slower earlier run must not overwrite a newer one
    const timer = setTimeout(async () => {
      if (formData.startDate && formData.endDate && (formData.rate || formData.annualSalary)) {
        setIsGeneratingPreview(true);
        try {
          const previewData = {
            ...formData, deductions, contributions, absencePlans, employerBenefits,
            logoDataUrl: logoPreview,
          };
          const numStubs = Math.max(1, ukCountPeriods(parseLocalDate(formData.startDate), parseLocalDate(formData.endDate), formData.payFrequency));
          const previews = await generateAllUkPreviewImages(previewData, selectedTemplate, numStubs);
          if (cancelled) return;
          setPdfPreviews(previews);
          if (previewPageIndex >= previews.length) setPreviewPageIndex(0);
        } catch (err) { console.error("Preview generation failed:", err); }
        if (!cancelled) setIsGeneratingPreview(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [formData, selectedTemplate, deductions, contributions, absencePlans, employerBenefits, logoPreview]);

  // ── Coupon state ──────────────────────────────────────────────────────────
  const [couponCode,         setCouponCode]         = useState("");
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponError,        setCouponError]        = useState("");

  // Auto-apply: as the user types or pastes a code we look it up (debounced)
  // and apply it with a toast — no Apply button.
  useEffect(() => {
    const code = couponCode.trim();
    if (!code || appliedDiscount) { setIsValidatingCoupon(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setIsValidatingCoupon(true);
      try {
        const { ok, data } = await nativePost(`${BACKEND_URL}/api/validate-coupon`, { code, generatorType: "uk-payslip" });
        if (cancelled) return;
        if (ok && data?.valid) {
          const base = stubPricing.price;
          const discountAmount = base * data.discountPercent / 100;
          setAppliedDiscount({ code: data.code, discountPercent: data.discountPercent, originalPrice: base, discountedPrice: parseFloat((base - discountAmount).toFixed(2)) });
          setCouponError("");
          showToast(`Coupon ${data.code} applied: ${data.discountPercent}% off!`, "success");
        } else {
          setCouponError(data?.detail || "Invalid coupon code");
          setAppliedDiscount(null);
        }
      } catch { if (!cancelled) { setCouponError("Connection error. Please try again."); setAppliedDiscount(null); } }
      finally { if (!cancelled) setIsValidatingCoupon(false); }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [couponCode]); // eslint-disable-line

  const removeCoupon = () => { setCouponCode(""); setAppliedDiscount(null); setCouponError(""); };

  // Admin-configured bundle pricing ("3 for $19.99") for this many stubs
  // one stub's price: the admin's (Preferences > Document prices), else 9.99; zero is free for anyone
  const unitPrice = useDocPrice("uk-payslip", 9.99);
  const stubPricing = useBundlePrice("uk-payslip", calculateNumStubs, unitPrice);

  // When the order is bigger than the plan's remaining downloads, the user
  // chooses: spend what's left and pay for the overflow, or buy the whole
  // order. The overflow part is priced like any checkout (bundles included).
  const subRemaining = hasActiveSubscription ? freeDownloadCount(user) : 0;
  const overflowCount = hasActiveSubscription && subRemaining !== -1
    ? Math.max(0, calculateNumStubs - Math.max(0, subRemaining)) : 0;
  const overflowPricing = useBundlePrice("uk-payslip", Math.max(1, overflowCount), unitPrice);
  const [overflowAsk, setOverflowAsk] = useState(false);
  // the account balance pays the whole order's price when no plan or
  // gifted downloads cover it
  const walletCovers = !ownDesign && !hasActiveSubscription && calculateNumStubs > 0 && walletBalance(user) >= (stubPricing.price || 0);
  const isFree = !ownDesign && unitPrice === 0;
  const covered = ownDesign || isFree || hasActiveSubscription || walletCovers;

  // ── Deductions helpers ────────────────────────────────────────────────────
  const addDeduction    = () => setDeductions(prev => [...prev, { id: Date.now(), type:"other", name:"", amount:"", isPercentage:false, preTax:false }]);
  const removeDeduction = (id) => setDeductions(prev => prev.filter(d => d.id !== id));
  const updateDeduction = (id, field, value) => setDeductions(prev => prev.map(d => {
    if (d.id !== id) return d;
    const updated = { ...d, [field]: value };
    if (field === "type") { const t = deductionTypes.find(x=>x.value===value); if (t) updated.preTax = t.preTax; }
    return updated;
  }));

  // ── Contributions helpers ─────────────────────────────────────────────────
  const addContribution    = () => setContributions(prev => [...prev, { id: Date.now(), type:"other", name:"", amount:"", isPercentage:false, preTax:false }]);
  const removeContribution = (id) => setContributions(prev => prev.filter(c => c.id !== id));
  const updateContribution = (id, field, value) => setContributions(prev => prev.map(c => {
    if (c.id !== id) return c;
    const updated = { ...c, [field]: value };
    if (field === "type") { const t = contributionTypes.find(x=>x.value===value); if (t) updated.preTax = t.preTax; }
    return updated;
  }));

  // ── Absence plans ─────────────────────────────────────────────────────────
  const addAbsencePlan    = () => setAbsencePlans(prev => [...prev, { id: Date.now(), description:"PTO Plan", accrued:"", reduced:"" }]);
  const removeAbsencePlan = (id) => setAbsencePlans(prev => prev.filter(p => p.id !== id));
  const updateAbsencePlan = (id, field, value) => setAbsencePlans(prev => prev.map(p => p.id===id ? {...p,[field]:value} : p));

  // ── Employer benefits ─────────────────────────────────────────────────────
  const addEmployerBenefit    = () => setEmployerBenefits(prev => [...prev, { id: Date.now(), type:"pension_match", name:"Employer Pension Contribution", amount:"", isPercentage:false, matchPercent:50, matchUpTo:6 }]);
  const removeEmployerBenefit = (id) => setEmployerBenefits(prev => prev.filter(b => b.id !== id));
  const updateEmployerBenefit = (id, field, value) => setEmployerBenefits(prev => prev.map(b => {
    if (b.id !== id) return b;
    const updated = { ...b, [field]: value };
    if (field === "type") { const t = employerBenefitTypes.find(x=>x.value===value); if (t && value !== "other") updated.name = t.label; }
    return updated;
  }));

  // ── Clear form ────────────────────────────────────────────────────────────
  const clearForm = async () => {
    if (!(await confirmAlert({ header: "Clear the form?", message: "All entered data will be lost.", confirmText: "Clear" }))) return;
    setFormData(defaultFormData);
    setDeductions([]); setContributions([]); setAbsencePlans([]); setEmployerBenefits([]); setHoursPerPeriod([]);
    setCompanyLogo(null); setLogoPreview(null);
    localStorage.removeItem(draftKey); localStorage.removeItem("ukPayslipTemplate"); localStorage.removeItem("ukPayslipCompanyLogo");
    showToast("Form cleared successfully", "success");
  };

  // ── Next: open preview modal ──────────────────────────────────────────────
  // A saved employee's Additional options come along with the pick: its
  // worker type where the template allows a choice, and each list that has
  // entries replaces the stub's.
  const applyEmployeeExtras = (emp) => {
    const fresh = (list) => (list || []).map((x, i) => ({ ...x, id: Date.now() + i }));
    if (emp?.deductions?.length) setDeductions(fresh(emp.deductions));
    if (emp?.contributions?.length) setContributions(fresh(emp.contributions));
    if (emp?.employerBenefits?.length) setEmployerBenefits(fresh(emp.employerBenefits));
    if (emp?.absencePlans?.length) setAbsencePlans(fresh(emp.absencePlans));
    if (emp?.workerType && workerMode === "both") handleWorkerTypeChange(emp.workerType);
  };

  const handleNext = async () => {
    // every required field (its label ends in *) must be filled before the preview;
    // the empties turn red and the first scrolls into view
    const missing = flagEmptyRequiredFields(formRef.current);
    if (missing) {
      showToast("Fill in the required fields first");
      missing.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    if (calculateNumStubs === 0) { showToast("Please configure at least one pay period"); return; }
    if (pdfPreviews.length === 0) {
      setIsGeneratingPreview(true);
      try {
        const previewData = { ...formData, deductions, contributions, absencePlans, employerBenefits, logoDataUrl: logoPreview };
        const previews = await generateAllUkPreviewImages(previewData, selectedTemplate, calculateNumStubs);
        setPdfPreviews(previews);
        setPreviewPageIndex(0);
      } catch (err) { console.error("Preview generation failed:", err); }
      setIsGeneratingPreview(false);
    }
    setPreviewModalOpen(true);
  };

  // ── Generate / download ───────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (calculateNumStubs === 0) { showToast("Please configure at least one pay period"); return; }
    if (covered) {
      if (overflowCount > 0) { setOverflowAsk(true); return; }
      await handleSubscriptionDownload(); return;
    }
    openCardCheckout();
  };

  // One-time purchase: open the in-app card checkout on top of the preview.
  // With `split`, the card covers only the stubs beyond the plan's remaining
  // downloads; the rest are consumed from the plan after the payment.
  const openCardCheckout = (split = null) => {
    const fullFormData = { ...formData, deductions, contributions, absencePlans, employerBenefits, companyLogo, logoDataUrl: logoPreview };
    setPendingCheckout({ fullFormData, split });
  };

  // Runs after the card payment succeeds — store the pending form data (same
  // keys the hosted-checkout flow used) and hand off to /payment-success,
  // which generates + downloads + emails and shows the success screen. The
  // webhook records the purchase from the payment-intent metadata.
  const handlePaymentSuccess = ({ email, paymentIntentId }) => {
    const { fullFormData } = pendingCheckout;
    localStorage.setItem("pendingUkPayslipData", JSON.stringify(fullFormData));
    localStorage.setItem("pendingUkPayslipTemplate", selectedTemplate);
    localStorage.setItem("pendingUkPayslipCount", calculateNumStubs.toString());
    localStorage.setItem("pendingCustomerEmail", email);
    // Reset this form only after the payment succeeds and the file downloads.
    localStorage.setItem("pendingResetKeys", JSON.stringify([draftKey, "ukPayslipTemplate", "ukPayslipCompanyLogo"]));
    const subUse = pendingCheckout?.split ? `&subUse=${pendingCheckout.split.subUse}` : "";
    navigate(`/payment-success?type=uk-payslip&count=${calculateNumStubs}&source=app&payment_intent=${encodeURIComponent(paymentIntentId)}${subUse}`);
  };

  const ionInputStyle = { marginBottom: 8 };

  const Wrap = embed ? Fragment : AppLayout;
  const wrapProps = embed ? {} : { fillHeight: true };
  return (
    <Wrap {...wrapProps}>
      {!embed && <AppSeo title="UK Payslip Generator" description="Create UK payslips with PAYE income tax and National Insurance calculated automatically. Instant PDF download for $9.99 per payslip." path="/app/uk-payslips" price={9.99} appName="MintSlip UK Payslip Generator" />}
      <div style={{ padding: 10, height: "100%", boxSizing: "border-box", ...(embed ? { display: "none" } : null) }}>
        <div style={{ background: "var(--ion-card-background)", borderRadius: 6, padding: "20px 20px 24px", height: "100%", overflowY: "auto", boxShadow: "0 2px 12px rgba(0,0,0,0.10)", boxSizing: "border-box" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
            {templateCards.map((company, cardIdx) => (
              <div key={company.id} className="app-sample-card ion-activatable"
                onClick={() => { handleTemplateChange(company.template); setFormModalOpen(true); }}
                style={{ animationDelay: `${cardIdx * 60}ms`, cursor: "pointer", borderRadius: 10, border: "1.5px solid var(--app-divider, rgba(0,0,0,0.12))", background: "var(--ion-card-background)", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", transition: "box-shadow 0.2s, transform 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
              >
                <ion-ripple-effect />
                <div style={{ position: "relative", background: "#fff", overflow: "hidden", minHeight: 160 }}>
                  {/* Template name badge over the preview (whodat/sample style) */}
                  <div style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: company.color, color: "#fff", padding: "4px 12px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, boxShadow: "0 2px 8px rgba(0,0,0,0.28)" }}>
                    {company.name}
                  </div>
                  {loadingPreviews ? (
                    <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden" }}>
                      <IonSkeletonText animated={true} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", margin: 0, borderRadius: 0, "--background": "rgba(0, 0, 0, 0.085)", "--background-rgb": "18, 18, 18" }} />
                    </div>
                  ) : templatePreviews[company.template] ? (
                    <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden", pointerEvents: "none" }}>
                      <img src={templatePreviews[company.template]} alt={`${company.name} template`} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block" }} />
                      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 56, background: "linear-gradient(to top, rgba(0,0,0,0.28), transparent)" }} />
                    </div>
                  ) : (
                    <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb" }}>
                      <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>Preview unavailable</span>
                    </div>
                  )}
                </div>
                <div style={{ padding: "8px 16px 12px", textAlign: "center", borderTop: "1px solid var(--app-divider, rgba(0,0,0,0.06))" }}>
                  <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--ion-color-primary)" }}>Select Template →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Form Modal (portalled to ion-app) ── */}
      {formModalOpen && createPortal(
        <div className="modal-backdrop" style={{ position: "fixed", inset: 0, zIndex: 10000, background: window.innerWidth >= 768 ? "rgba(0,0,0,0.5)" : "var(--ion-background-color, #f2f2f7)", display: "flex", alignItems: window.innerWidth >= 768 ? "center" : "stretch", justifyContent: window.innerWidth >= 768 ? "center" : "stretch" }}>
          <div className="modal-slide-up" style={{ background: "var(--ion-background-color, #f2f2f7)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: window.innerWidth >= 768 ? 600 : "100%", height: window.innerWidth >= 768 ? "auto" : "100%", maxHeight: window.innerWidth >= 768 ? "90vh" : "100%", overflow: "hidden" }}>
          <IonHeader>
            <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
              <IonButtons slot="start">
                <IonButton fill="clear" shape="round" onClick={(e) => dismissModal(() => (embed && onEmbedClose ? onEmbedClose() : setFormModalOpen(false)), e)}>
                  <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                    <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                  </span>
                </IonButton>
              </IonButtons>
              <IonTitle style={{ fontWeight: 700 }}>UK Payslip Details</IonTitle>
              <IonButtons slot="end">
                <IonButton fill="clear" shape="round" onClick={handleNext} style={{ opacity: isGeneratingPreview ? 0.6 : 1 }}>
                  <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1rem", color: "var(--ion-color-success)" }}>
                    {isGeneratingPreview
                      ? <IonSpinner name="crescent" style={{ width: 18, height: 18, color: "var(--ion-color-medium)" }} />
                      : <IonIcon icon={checkmarkOutline} className="bold-icon" style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />}
                  </span>
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 40px" }}>
            <div ref={formRef} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* ── Worker Type ── */}
              <div style={cardStyle}>
                <span style={sectionHeadingStyle}>Worker Type</span>
                <div>
                  {workerMode === "both" ? (
                    <IonSegment mode="ios" value={formData.workerType} onIonChange={e => handleWorkerTypeChange(e.detail.value)}>
                      <IonSegmentButton value="employee" layout="icon-start">
                        <IonIcon icon={personOutline} />
                        <IonLabel>Employee</IonLabel>
                      </IonSegmentButton>
                      <IonSegmentButton value="contractor" layout="icon-start">
                        <IonIcon icon={briefcaseOutline} />
                        <IonLabel>Contractor</IonLabel>
                      </IonSegmentButton>
                    </IonSegment>
                  ) : (
                    <IonItem lines="none">
                      <IonLabel>{workerMode === "contractor" ? "Contractor" : "Employee"} (only option for this template)</IonLabel>
                    </IonItem>
                  )}
                </div>
              </div>

              {/* ── Template ── */}
              <div style={cardStyle}>
                <span style={sectionHeadingStyle}>Template</span>
                <div>
                  <IonSelect fill="outline" labelPlacement="floating" interface="popover" label="Template" value={selectedTemplate} onIonChange={e => handleTemplateChange(e.detail.value)} style={ionInputStyle} className="saved-asset-select">
                    <IonSelectOption value="template-h">OnPay Style</IonSelectOption>
                    <IonSelectOption value="template-s">Sage Style</IonSelectOption>
                    {customTemplates.map(t => <IonSelectOption key={t.id} value={`custom:${t.id}`}>{t.name}</IonSelectOption>)}
                    {userTemplates.map(t => <IonSelectOption key={t.id} value={`usercustom:${t.id}`}>{t.name}</IonSelectOption>)}
                  </IonSelect>

                  {/* Customer color choice (offered by some custom templates) */}
                  {accentOption?.enabled && (
                    <div style={{ marginTop: 12 }}>
                      <p style={{ fontWeight: 600, marginBottom: 8 }}>Document Color</p>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        {[["", accentOption.baseColor || "#064e3b", "Default"],
                          ...(accentOption.swatches || [])
                            .filter(c => String(c).toLowerCase() !== String(accentOption.baseColor || "").toLowerCase())
                            .map(c => [c, c, c])].map(([val, color, label]) => (
                          <button key={label} type="button" title={label}
                            onClick={() => setFormData(prev => ({ ...prev, accentColor: val }))}
                            style={{ width: 30, height: 30, borderRadius: "50%", background: color, padding: 0, cursor: "pointer",
                              border: (formData.accentColor || "") === val ? "3px solid var(--ion-color-primary)" : "2px solid var(--app-divider, rgba(0,0,0,0.15))" }} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Logo upload */}
                  <div style={{ marginTop: 12 }}>
                    {!hasLogoSelect && <p style={{ fontWeight: 600, marginBottom: 8 }}>Company Logo (optional)</p>}
                    <SavedLogoSelect current={logoPreview} label="Saved company logo" style={{ marginBottom: 8 }}
                      onApply={async (a) => {
                        if (!a) { removeLogo(); return; }
                        const resized = await resizeImageToFit(a.data, 360, 105);
                        localStorage.setItem("ukPayslipCompanyLogo", resized);
                        setCompanyLogo(resized); setLogoPreview(resized); setLogoError("");
                      }} />
                    <div
                      onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
                      onDrop={handleLogoDrop}
                    >
                      {logoPreview ? (
                        <UploadedImage src={logoPreview} onRemove={removeLogo} />
                      ) : (
                        <UploadDropzone
                          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                          style={{ borderColor: isDragging ? "var(--ion-color-primary)" : logoError ? "var(--ion-color-danger)" : undefined }}
                          onFile={(f) => validateAndProcessLogo(f)}
                        />
                      )}
                    </div>
                    {logoError && <p style={{ color: "var(--ion-color-danger)", fontSize: "0.75rem", marginTop: 4 }}>{logoError}</p>}
                  </div>
                </div>
              </div>

              {/* ── Employee Info ── */}
              <div style={cardStyle}>
                <span style={sectionHeadingStyle}>{formData.workerType === "contractor" ? "Contractor" : "Employee"} Information <IonNote color="danger" className="required-note">(Required)</IonNote></span>
                <div>
                  <IonGrid>
                    <IonRow>
                      <IonCol size="12" sizeMd="6">
                        <IonInput fill="outline" labelPlacement="floating" label={formData.workerType === "contractor" ? "Contractor Name *" : "Employee Name *"} name="name" value={formData.name} onIonInput={e => handleChange({ target: { name:"name", value: e.detail.value } })} style={ionInputStyle} />
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonInput fill="outline" labelPlacement="floating" label="National Insurance Number *" value={formData.niNumber} onIonInput={e => handleNiNumberChange(e.detail.value)} placeholder="AB 12 34 56 C" style={ionInputStyle} className={validationErrors.niNumber ? "ion-invalid ion-touched" : undefined} />
                        {validationErrors.niNumber && <IonNote color="danger" style={{ fontSize: "0.75rem" }}>{validationErrors.niNumber}</IonNote>}
                      </IonCol>
                      {selectedTemplate === "template-c" && (
                        <IonCol size="12" sizeMd="6">
                          <IonInput fill="outline" labelPlacement="floating" label="Employee ID *" name="employeeId" value={formData.employeeId} onIonInput={e => handleChange({ target: { name:"employeeId", value: e.detail.value } })} placeholder="100012345" style={ionInputStyle} />
                        </IonCol>
                      )}
                      <IonCol size="12" sizeMd="6">
                        <IonSelect fill="outline" labelPlacement="floating" interface="popover" label="Payment Method *" value={formData.paymentMethod || "bacs"} onIonChange={e => setFormData(prev => ({ ...prev, paymentMethod: e.detail.value }))} style={ionInputStyle}>
                          {UK_PAYMENT_METHODS.map((pm) => <IonSelectOption key={pm.code} value={pm.code}>{pm.menu}</IonSelectOption>)}
                        </IonSelect>
                      </IonCol>
                      {/* cash and cheque name no account */}
                      {(UK_PAYMENT_METHODS.find((p) => p.code === (formData.paymentMethod || "bacs"))?.toBank !== false) && (<>
                      <IonCol size="12" sizeMd="6">
                        <IonInput fill="outline" labelPlacement="floating" label="Bank Name *" name="bankName" value={formData.bankName} onIonInput={e => handleChange({ target: { name:"bankName", value: e.detail.value } })} style={ionInputStyle} />
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonInput fill="outline" labelPlacement="floating" label="Last 4 of Bank Account *" value={formData.bank} onIonInput={e => handleBankChange(e.detail.value)} maxlength={4} placeholder="5678" style={ionInputStyle} className={validationErrors.bank ? "ion-invalid ion-touched" : undefined} />
                        {validationErrors.bank && <IonNote color="danger" style={{ fontSize: "0.75rem" }}>{validationErrors.bank}</IonNote>}
                      </IonCol>
                      </>)}
                      <IonCol size="12">
                        <IonInput fill="outline" labelPlacement="floating" label="Address *" name="address" value={formData.address} onIonInput={e => handleChange({ target: { name:"address", value: e.detail.value } })} style={ionInputStyle} />
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonInput fill="outline" labelPlacement="floating" label="City *" name="city" value={formData.city} onIonInput={e => handleChange({ target: { name:"city", value: e.detail.value } })} style={ionInputStyle} />
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonInput fill="outline" labelPlacement="floating" label="Postcode *" value={formData.postcode} onIonInput={e => handlePostcodeChange(e.detail.value)} placeholder="SW1A 1AA" style={ionInputStyle} className={validationErrors.postcode ? "ion-invalid ion-touched" : undefined} />
                        {validationErrors.postcode && <IonNote color="danger" style={{ fontSize: "0.75rem" }}>{validationErrors.postcode}</IonNote>}
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </div>
                {/* A row flush at the foot of the card opens the business's
                    saved employees (Pro/Enterprise); picking one fills what
                    maps cleanly, the NI number and postcode stay by hand.
                    Clear puts the pay defaults back. */}
                <SelectExistingEmployee
                  country="uk"
                  onApply={(emp) => {
                    setFormData((prev) => ({
                      ...prev,
                      name: emp?.name || "", employeeId: emp?.employeeId || "",
                      niNumber: emp?.niNumber || "", postcode: emp?.postcode || "",
                      address: emp?.address || "", city: emp?.city || "",
                      bankName: emp?.bankName || "", bank: emp?.bankLast4 || "",
                      payType: emp?.payType || "hourly", rate: emp?.rate || "", annualSalary: emp?.annualSalary || "",
                      payFrequency: emp?.payFrequency || "biweekly", payDay: emp?.payDay || "Friday", hireDate: emp?.hireDate || "",
                    }));
                    applyEmployeeExtras(emp);
                  }}
                />
              </div>

              {/* ── Company Info ── */}
              <div style={cardStyle}>
                <span style={sectionHeadingStyle}>Company Information <IonNote color="danger" className="required-note">(Required)</IonNote></span>
                <div>
                  <IonGrid>
                    <IonRow>
                      <IonCol size="12" sizeMd="6">
                        <IonInput fill="outline" labelPlacement="floating" label="Company Name *" name="company" value={formData.company} onIonInput={e => handleChange({ target: { name:"company", value: e.detail.value } })} style={ionInputStyle} />
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonInput fill="outline" labelPlacement="floating" label="Company Phone *" value={formData.companyPhone} onIonInput={e => handleCompanyPhoneChange(e.detail.value)} placeholder="(555) 123-4567" style={ionInputStyle} className={validationErrors.companyPhone ? "ion-invalid ion-touched" : undefined} />
                        {validationErrors.companyPhone && <IonNote color="danger" style={{ fontSize: "0.75rem" }}>{validationErrors.companyPhone}</IonNote>}
                      </IonCol>
                      <IonCol size="12">
                        <IonInput fill="outline" labelPlacement="floating" label="Company Address *" name="companyAddress" value={formData.companyAddress} onIonInput={e => handleChange({ target: { name:"companyAddress", value: e.detail.value } })} style={ionInputStyle} />
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonInput fill="outline" labelPlacement="floating" label="Company City *" name="companyCity" value={formData.companyCity} onIonInput={e => handleChange({ target: { name:"companyCity", value: e.detail.value } })} style={ionInputStyle} />
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonInput fill="outline" labelPlacement="floating" label="Company Postcode *" value={formData.companyPostcode} onIonInput={e => handleCompanyPostcodeChange(e.detail.value)} placeholder="SW1A 1AA" style={ionInputStyle} className={validationErrors.companyPostcode ? "ion-invalid ion-touched" : undefined} />
                        {validationErrors.companyPostcode && <IonNote color="danger" style={{ fontSize: "0.75rem" }}>{validationErrors.companyPostcode}</IonNote>}
                      </IonCol>

                      {["template-h", "template-s"].includes(selectedTemplate) && (
                        <>
                          <IonCol size="12"><p style={{ fontWeight: 600, margin: "8px 0" }}>OnPay Document Info</p></IonCol>
                          <IonCol size="12" sizeMd="6">
                            <IonInput fill="outline" labelPlacement="floating" label="Employee ID (EMP#) *" name="employeeId" value={formData.employeeId} onIonInput={e => handleChange({ target: { name:"employeeId", value: e.detail.value } })} maxlength={10} placeholder="Auto-generated if empty" style={ionInputStyle} />
                          </IonCol>
                        </>
                      )}
                    </IonRow>
                  </IonGrid>
                </div>
              </div>

              {/* ── Pay Details ── */}
              <div style={cardStyle}>
                <span style={sectionHeadingStyle}>Pay Details <IonNote color="danger" className="required-note">(Required)</IonNote></span>
                <div>
                  <IonSegment mode="ios" value={formData.payType} onIonChange={e => setFormData(prev => ({ ...prev, payType: e.detail.value }))} style={{ marginBottom: 12 }}>
                    <IonSegmentButton value="hourly"><IonLabel>Hourly</IonLabel></IonSegmentButton>
                    <IonSegmentButton value="salary" disabled={!canUseSalary}><IonLabel>Salary</IonLabel></IonSegmentButton>
                  </IonSegment>
                  {!canUseSalary && <IonNote style={{ fontSize: "0.75rem", color: "var(--ion-color-warning)", display: "block", marginBottom: 8 }}>Salary not available for contractors on Gusto template</IonNote>}

                  <IonGrid>
                    <IonRow>
                      <IonCol size="12" sizeMd="6">
                        <IonDateInput label={formData.workerType === "contractor" ? "Start Date *" : "Hire Date *"} value={formData.hireDate} onChange={v => handleChange({ target: { name:"hireDate", value: v } })} style={ionInputStyle} />
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        {formData.payType === "hourly" ? (
                          <IonInput fill="outline" labelPlacement="floating" label="Hourly Rate (GBP £) *" type="number" name="rate" value={formData.rate} onIonInput={e => handleChange({ target: { name:"rate", value: e.detail.value } })} style={ionInputStyle} />
                        ) : (
                          <IonInput fill="outline" labelPlacement="floating" label="Annual Salary (GBP £) *" type="number" name="annualSalary" value={formData.annualSalary} onIonInput={e => handleChange({ target: { name:"annualSalary", value: e.detail.value } })} style={ionInputStyle} />
                        )}
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonSelect fill="outline" labelPlacement="floating" interface="popover" label="Pay Frequency *" value={formData.payFrequency} onIonChange={e => setFormData(prev => ({ ...prev, payFrequency: e.detail.value }))} style={ionInputStyle}>
                          {UK_PAY_FREQUENCIES.map(([v, l]) => <IonSelectOption key={v} value={v}>{l}</IonSelectOption>)}
                        </IonSelect>
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonSelect fill="outline" labelPlacement="floating" interface="popover" label="Pay Day *" value={formData.payDay} onIonChange={e => setFormData(prev => ({ ...prev, payDay: e.detail.value }))} style={ionInputStyle}>
                          {["Monday","Tuesday","Wednesday","Thursday","Friday"].map(d => <IonSelectOption key={d} value={d}>{d}</IonSelectOption>)}
                        </IonSelect>
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonDateInput label="Period Start Date *" value={formData.startDate} onChange={v => handleChange({ target: { name:"startDate", value: v } })} style={ionInputStyle} />
                      </IonCol>
                      <IonCol size="12" sizeMd="6">
                        <IonDateInput label="Period End Date *" value={formData.endDate} onChange={v => handleChange({ target: { name:"endDate", value: v } })} style={ionInputStyle} />
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                  {calculateNumStubs > 0 && (
                    <IonNote style={{ display: "block", marginTop: 8 }}>
                      This will generate <strong>{calculateNumStubs}</strong> paystub{calculateNumStubs > 1 ? "s" : ""}
                    </IonNote>
                  )}

                  {/* Hours per pay period */}
                  {formData.payType === "hourly" && payPeriods.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <IonButton fill="clear" size="small" color="dark" className="section-expander" onClick={() => setHoursExpanded(v => !v)} aria-expanded={hoursExpanded}>
                        <IonIcon slot="start" icon={hoursExpanded ? chevronDownOutline : chevronForwardOutline} />
                        Adjust Pay Period Details
                      </IonButton>
                      {hoursExpanded && (
                        <div>
                          {formData.workerType === "contractor" && (
                            <IonNote color="warning" style={{ display: "block", marginBottom: 8, fontSize: "0.75rem" }}>
                              Contractors are not legally entitled to overtime pay.
                            </IonNote>
                          )}
                          {payPeriods.map((period, index) => (
                            <div key={index} className="period-card">
                              <div className="admin-subbar sheet-card-head period-head">Pay Period {index + 1}: {period.label}</div>
                              <div className="period-form">
                                    <IonDateInput label="Period Start" value={hoursPerPeriod[index]?.startDate || period.start} onChange={v => handlePeriodHoursChange(index, "startDate", v)} />
                                    <IonDateInput label="Period End" value={hoursPerPeriod[index]?.endDate || period.end} onChange={v => handlePeriodHoursChange(index, "endDate", v)} />
                                    <IonDateInput label="Pay Date" value={hoursPerPeriod[index]?.payDate || period.pay} onChange={v => handlePeriodHoursChange(index, "payDate", v)} />
                                    <IonInput {...FLOAT} label="Regular Hours" type="number" value={hoursPerPeriod[index]?.hours ?? ukDefaultHours(formData.payFrequency)} onIonInput={e => handlePeriodHoursChange(index, "hours", e.detail.value)} />
                                  {formData.workerType === "employee" && (
                                      <IonInput {...FLOAT} label="Overtime Hours" type="number" value={hoursPerPeriod[index]?.overtime ?? 0} onIonInput={e => handlePeriodHoursChange(index, "overtime", e.detail.value)} />
                                  )}
                                    <IonInput {...FLOAT} label="Commission (GBP £)" type="number" value={hoursPerPeriod[index]?.commission ?? 0} onIonInput={e => handlePeriodHoursChange(index, "commission", e.detail.value)} />
                                  {["template-h", "template-s"].includes(selectedTemplate) && (
                                    <>
                                        <IonInput {...FLOAT} label={formData.paymentMethod === "cheque" ? "Cheque Number" : "Payslip Number"} value={hoursPerPeriod[index]?.checkNumber ?? ""} onIonInput={e => handlePeriodHoursChange(index, "checkNumber", e.detail.value)} />
                                        <IonInput {...FLOAT} className="admin-field period-wide" label="Memo" value={hoursPerPeriod[index]?.memo ?? ""} onIonInput={e => handlePeriodHoursChange(index, "memo", e.detail.value)} />
                                    </>
                                  )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Additional options: the optional sections (tax settings,
                     deductions, contributions, benefits, absence plans),
                     folded until asked for ── */}
              <IonButton fill="clear" size="small" color="dark" className="section-expander" onClick={() => setMoreOpen(v => !v)} aria-expanded={moreOpen}>
                <IonIcon slot="start" icon={moreOpen ? chevronDownOutline : chevronForwardOutline} />
                Additional options
              </IonButton>
              {moreOpen && (
                <>
                  {/* ── Tax Settings ── */}
                  <div style={cardStyle}>
                    <span style={sectionHeadingStyle}>Tax Settings <IonNote color="medium" className="optional-note">(Optional)</IonNote></span>
                    <div>
                      <IonGrid>
                        <IonRow>
                          <IonCol size="12" sizeMd="6">
                            <IonInput fill="outline" labelPlacement="floating" label="Tax Code" name="taxCode" value={formData.taxCode} onIonInput={e => handleChange({ target: { name:"taxCode", value: e.detail.value } })} placeholder="1257L" style={ionInputStyle} />
                          </IonCol>
                          <IonCol size="12" sizeMd="6">
                            <IonSelect fill="outline" labelPlacement="floating" interface="popover" label="NI Category" value={formData.niCategory} onIonChange={e => setFormData(prev => ({ ...prev, niCategory: e.detail.value }))} style={ionInputStyle}>
                              {NI_CATEGORIES.map(([v, l]) => <IonSelectOption key={v} value={v}>{l}</IonSelectOption>)}
                            </IonSelect>
                          </IonCol>
                          <IonCol size="12" sizeMd="6">
                            <IonSelect fill="outline" labelPlacement="floating" interface="popover" label="Student Loan" value={formData.studentLoanPlan} onIonChange={e => setFormData(prev => ({ ...prev, studentLoanPlan: e.detail.value }))} style={ionInputStyle}>
                              {STUDENT_LOAN_PLANS.map(([v, l]) => <IonSelectOption key={v} value={v}>{l}</IonSelectOption>)}
                            </IonSelect>
                          </IonCol>
                          <IonCol size="6" sizeMd="3">
                            <IonInput fill="outline" labelPlacement="floating" label="PAYE (%)" type="number" inputmode="decimal" placeholder="Auto" name="payeRate" value={formData.payeRate} onIonInput={e => handleChange({ target: { name:"payeRate", value: e.detail.value } })} style={ionInputStyle} />
                          </IonCol>
                          <IonCol size="6" sizeMd="3">
                            <IonInput fill="outline" labelPlacement="floating" label="NI (%)" type="number" inputmode="decimal" placeholder="Auto" name="niRate" value={formData.niRate} onIonInput={e => handleChange({ target: { name:"niRate", value: e.detail.value } })} style={ionInputStyle} />
                          </IonCol>
                          <IonCol size="12">
                            <p style={{ margin: "0 2px 4px", fontSize: "0.75rem", color: "var(--ion-color-medium)" }}>Blank rates use the automatic calculation: the tax code's allowance and bands, and the NI thresholds for the category. A number here is a flat rate.</p>
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                    </div>
                  </div>

                  {/* ── Deductions ── */}
                  <div style={cardStyle}>
                    <span style={sectionHeadingStyle}>Deductions <IonNote color="medium" className="optional-note">(Optional)</IonNote> {deductions.length > 0 && <IonBadge color="primary">{deductions.length}</IonBadge>}</span>
                    <div>
                      {deductions.map((d, i) => (
                        <div key={d.id} className="period-card">
                          <div className="admin-subbar sheet-card-head period-head with-action">
                            <span>Deduction {i + 1}{(deductionTypes.find(t => t.value === d.type) || {}).label ? `: ${(deductionTypes.find(t => t.value === d.type) || {}).label}` : ""}</span>
                            <IonButton fill="clear" color="danger" size="small" className="period-head-remove" onClick={() => removeDeduction(d.id)} aria-label="Remove deduction">
                              <IonIcon slot="icon-only" icon={trashOutline} />
                            </IonButton>
                          </div>
                          <div className="period-form">
                            <IonSelect {...FLOAT} interface="popover" label="Type" value={d.type} onIonChange={e => updateDeduction(d.id, "type", e.detail.value)}>
                              {deductionTypes.map(t => <IonSelectOption key={t.value} value={t.value}>{t.label}</IonSelectOption>)}
                            </IonSelect>
                            <IonInput {...FLOAT} label="Name / Description" value={d.name} onIonInput={e => updateDeduction(d.id, "name", e.detail.value)} />
                            <IonInput {...FLOAT} label={d.isPercentage ? "Amount (%)" : "Amount (GBP £)"} type="number" inputmode="decimal" value={d.amount} onIonInput={e => updateDeduction(d.id, "amount", e.detail.value)} />
                            <IonSelect {...FLOAT} interface="popover" label="Unit" value={d.isPercentage ? "percent" : "amount"} onIonChange={e => updateDeduction(d.id, "isPercentage", e.detail.value === "percent")}>
                              <IonSelectOption value="amount">Dollars ($)</IonSelectOption>
                              <IonSelectOption value="percent">Percent (%)</IonSelectOption>
                            </IonSelect>
                            <IonItem lines="none" className="admin-field period-wide period-toggle">
                              <IonLabel style={{ fontSize: "0.85rem" }}>Pre-Tax Deduction</IonLabel>
                              <IonToggle slot="end" checked={d.preTax} onIonChange={e => { Haptics.impact({ style: ImpactStyle.Light }).catch(() => {}); updateDeduction(d.id, "preTax", e.detail.checked); }} />
                            </IonItem>
                          </div>
                        </div>
                      ))}
                      <IonItem button detail lines="none" className="card-foot-row ion-text-center" onClick={addDeduction}>
                        <IonLabel color="primary">Add Deduction</IonLabel>
                      </IonItem>
                    </div>
                  </div>

                  {/* ── Contributions ── */}
                  <div style={cardStyle}>
                    <span style={sectionHeadingStyle}>Contributions (Pension, etc.) <IonNote color="medium" className="optional-note">(Optional)</IonNote> {contributions.length > 0 && <IonBadge color="secondary">{contributions.length}</IonBadge>}</span>
                    <div>
                      {contributions.map((c, i) => (
                        <div key={c.id} className="period-card">
                          <div className="admin-subbar sheet-card-head period-head with-action">
                            <span>Contribution {i + 1}{(contributionTypes.find(t => t.value === c.type) || {}).label ? `: ${(contributionTypes.find(t => t.value === c.type) || {}).label}` : ""}</span>
                            <IonButton fill="clear" color="danger" size="small" className="period-head-remove" onClick={() => removeContribution(c.id)} aria-label="Remove contribution">
                              <IonIcon slot="icon-only" icon={trashOutline} />
                            </IonButton>
                          </div>
                          <div className="period-form">
                            <IonSelect {...FLOAT} interface="popover" label="Type" value={c.type} onIonChange={e => updateContribution(c.id, "type", e.detail.value)}>
                              {contributionTypes.map(t => <IonSelectOption key={t.value} value={t.value}>{t.label}</IonSelectOption>)}
                            </IonSelect>
                            <IonInput {...FLOAT} label="Name / Description" value={c.name} onIonInput={e => updateContribution(c.id, "name", e.detail.value)} />
                            <IonInput {...FLOAT} label={c.isPercentage ? "Amount (%)" : "Amount (GBP £)"} type="number" inputmode="decimal" value={c.amount} onIonInput={e => updateContribution(c.id, "amount", e.detail.value)} />
                            <IonSelect {...FLOAT} interface="popover" label="Unit" value={c.isPercentage ? "percent" : "amount"} onIonChange={e => updateContribution(c.id, "isPercentage", e.detail.value === "percent")}>
                              <IonSelectOption value="amount">Dollars ($)</IonSelectOption>
                              <IonSelectOption value="percent">Percent (%)</IonSelectOption>
                            </IonSelect>
                            <IonItem lines="none" className="admin-field period-wide period-toggle">
                              <IonLabel style={{ fontSize: "0.85rem" }}>Pre-Tax Contribution</IonLabel>
                              <IonToggle slot="end" checked={c.preTax} onIonChange={e => { Haptics.impact({ style: ImpactStyle.Light }).catch(() => {}); updateContribution(c.id, "preTax", e.detail.checked); }} />
                            </IonItem>
                          </div>
                        </div>
                      ))}
                      <IonItem button detail lines="none" className="card-foot-row ion-text-center" onClick={addContribution}>
                        <IonLabel color="primary">Add Contribution</IonLabel>
                      </IonItem>
                    </div>
                  </div>

                  {/* ── Employer Benefits (Template C only) ── */}
                  {selectedTemplate === "template-c" && formData.workerType === "employee" && (
                    <div style={cardStyle}>
                      <span style={sectionHeadingStyle}>Employer Benefits <IonNote color="medium" className="optional-note">(Optional)</IonNote> {employerBenefits.length > 0 && <IonBadge color="tertiary">{employerBenefits.length}</IonBadge>}</span>
                      <div>
                        {employerBenefits.map((b, i) => (
                          <div key={b.id} className="period-card">
                            <div className="admin-subbar sheet-card-head period-head with-action">
                              <span>Benefit {i + 1}{(employerBenefitTypes.find(t => t.value === b.type) || {}).label ? `: ${(employerBenefitTypes.find(t => t.value === b.type) || {}).label}` : ""}</span>
                              <IonButton fill="clear" color="danger" size="small" className="period-head-remove" onClick={() => removeEmployerBenefit(b.id)} aria-label="Remove benefit">
                                <IonIcon slot="icon-only" icon={trashOutline} />
                              </IonButton>
                            </div>
                            <div className="period-form">
                              <IonSelect {...FLOAT} className={`admin-field${b.type === "pension_match" ? " period-wide" : ""}`} interface="popover" label="Benefit Type" value={b.type} onIonChange={e => updateEmployerBenefit(b.id, "type", e.detail.value)}>
                                {employerBenefitTypes.map(t => <IonSelectOption key={t.value} value={t.value}>{t.label}</IonSelectOption>)}
                              </IonSelect>
                              {b.type === "pension_match" ? (
                                <>
                                  <IonInput {...FLOAT} label="Match %" type="number" inputmode="decimal" value={b.matchPercent || 50} onIonInput={e => updateEmployerBenefit(b.id, "matchPercent", e.detail.value)} />
                                  <IonInput {...FLOAT} label="Up to % of Pay" type="number" inputmode="decimal" value={b.matchUpTo || 6} onIonInput={e => updateEmployerBenefit(b.id, "matchUpTo", e.detail.value)} />
                                </>
                              ) : (
                                <>
                                  <IonInput {...FLOAT} label="Description" value={b.name} onIonInput={e => updateEmployerBenefit(b.id, "name", e.detail.value)} />
                                  <IonInput {...FLOAT} label={b.isPercentage ? "Amount (%)" : "Amount (GBP £)"} type="number" inputmode="decimal" value={b.amount} onIonInput={e => updateEmployerBenefit(b.id, "amount", e.detail.value)} />
                                  <IonSelect {...FLOAT} interface="popover" label="Unit" value={b.isPercentage ? "percent" : "amount"} onIonChange={e => updateEmployerBenefit(b.id, "isPercentage", e.detail.value === "percent")}>
                                    <IonSelectOption value="amount">Pounds (GBP £)</IonSelectOption>
                                    <IonSelectOption value="percent">Percent (%)</IonSelectOption>
                                  </IonSelect>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                        <IonItem button detail lines="none" className="card-foot-row ion-text-center" onClick={addEmployerBenefit}>
                          <IonLabel color="primary">Add Employer Benefit</IonLabel>
                        </IonItem>
                      </div>
                    </div>
                  )}

                  {/* ── Absence Plans (Template C / H) ── */}
                  {(selectedTemplate === "template-c" || ["template-h", "template-s"].includes(selectedTemplate)) && formData.workerType === "employee" && (
                    <div style={cardStyle}>
                      <span style={sectionHeadingStyle}>Absence Plans (PTO, Vacation, Sick) <IonNote color="medium" className="optional-note">(Optional)</IonNote> {absencePlans.length > 0 && <IonBadge color="medium">{absencePlans.length}</IonBadge>}</span>
                      <div>
                        {absencePlans.map((p, i) => (
                          <div key={p.id} className="period-card">
                            <div className="admin-subbar sheet-card-head period-head with-action">
                              <span>Absence Plan {i + 1}{p.description ? `: ${p.description}` : ""}</span>
                              <IonButton fill="clear" color="danger" size="small" className="period-head-remove" onClick={() => removeAbsencePlan(p.id)} aria-label="Remove absence plan">
                                <IonIcon slot="icon-only" icon={trashOutline} />
                              </IonButton>
                            </div>
                            <div className="period-form">
                              <IonInput {...FLOAT} className="admin-field period-wide" label="Plan Name" value={p.description} onIonInput={e => updateAbsencePlan(p.id, "description", e.detail.value)} placeholder="e.g., PTO Plan" />
                              <IonInput {...FLOAT} label="Accrued (hrs)" type="number" inputmode="decimal" value={p.accrued} onIonInput={e => updateAbsencePlan(p.id, "accrued", e.detail.value)} />
                              <IonInput {...FLOAT} label="Reduced (hrs)" type="number" inputmode="decimal" value={p.reduced} onIonInput={e => updateAbsencePlan(p.id, "reduced", e.detail.value)} />
                            </div>
                          </div>
                        ))}
                        <IonItem button detail lines="none" className="card-foot-row ion-text-center" onClick={addAbsencePlan}>
                          <IonLabel color="primary">Add Absence Plan</IonLabel>
                        </IonItem>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              <IonButton expand="block" color="light" onClick={handleNext} disabled={isGeneratingPreview}>
                {isGeneratingPreview ? <IonSpinner name="crescent" /> : <><IonIcon icon={eyeOutline} slot="start" />Preview</>}
              </IonButton>
              <IonButton expand="block" color="danger" onClick={clearForm}><IonIcon icon={trashOutline} slot="start" />Clear Form</IonButton>
            </div>
          </div>
          </div>
        </div>,
        document.querySelector("ion-app") || document.body
      )}

      {/* ── Preview Modal (portalled to ion-app) ── */}
      {previewModalOpen && createPortal(
        <div className="modal-backdrop" style={{ position: "fixed", inset: 0, zIndex: 10001, background: window.innerWidth >= 768 ? "rgba(0,0,0,0.5)" : "var(--ion-background-color, #f2f2f7)", display: "flex", alignItems: window.innerWidth >= 768 ? "center" : "stretch", justifyContent: window.innerWidth >= 768 ? "center" : "stretch" }}>
          <div className="modal-slide-up" style={{ background: "var(--ion-background-color, #f2f2f7)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: window.innerWidth >= 768 ? 600 : "100%", height: window.innerWidth >= 768 ? "auto" : "100%", maxHeight: window.innerWidth >= 768 ? "90vh" : "100%", overflow: "hidden" }}>
          <IonHeader>
            <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
              <IonButtons slot="start">
                <IonButton fill="clear" shape="round" onClick={(e) => dismissModal(() => setPreviewModalOpen(false), e)}>
                  <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                    <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                  </span>
                </IonButton>
              </IonButtons>
              <IonTitle style={{ fontWeight: 700 }}>
                Preview {pdfPreviews.length > 1 ? `(${previewPageIndex + 1} of ${pdfPreviews.length})` : ""}
              </IonTitle>
            </IonToolbar>
          </IonHeader>
          <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
              {isGeneratingPreview ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320, background: "var(--ion-color-step-100)", borderRadius: 8 }}>
                  <IonSpinner name="crescent" style={{ marginBottom: 8 }} />
                  <span style={{ fontSize: "0.8rem", color: "var(--ion-color-medium)" }}>Generating preview…</span>
                </div>
              ) : pdfPreviews.length > 0 && pdfPreviews[previewPageIndex] ? (
                <>
                  <PreviewPager pages={pdfPreviews} index={previewPageIndex} onIndexChange={setPreviewPageIndex} altPrefix="UK payslip preview" />
                  <p style={{ textAlign: "center", fontSize: "0.75rem", color: "var(--ion-color-medium)", marginTop: 8 }}>Watermark removed after payment</p>
                </>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320, background: "var(--ion-color-step-100)", borderRadius: 8, border: "2px dashed var(--ion-color-light-shade)" }}>
                  <IonIcon icon={eyeOutline} style={{ fontSize: "2.5rem", color: "var(--ion-color-medium)", marginBottom: 8 }} />
                  <p style={{ fontSize: "0.8rem", color: "var(--ion-color-medium)", textAlign: "center", margin: 0 }}>No preview available yet</p>
                </div>
              )}

              {!covered && calculateNumStubs > 0 && (
                <div style={{ marginTop: 20 }}>
                  {!appliedDiscount ? (
                    <>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <IonInput
                          fill="outline" labelPlacement="floating" label="Coupon code"
                          value={couponCode}
                          onIonInput={e => { setCouponCode((e.detail.value || "").toUpperCase()); setCouponError(""); }}
                          style={{ flex: 1, fontFamily: "monospace" }}
                        />
                        {isValidatingCoupon && <IonSpinner name="crescent" style={{ flexShrink: 0 }} />}
                      </div>
                      {couponError && <IonNote color="danger" style={{ display: "block", marginTop: 4, fontSize: "0.75rem" }}>{couponError}</IonNote>}
                    </>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "var(--ion-color-success)", borderRadius: 6 }}>
                      <span style={{ color: "var(--ion-color-success-contrast)", fontWeight: 600, fontSize: "0.85rem" }}>
                        {appliedDiscount.code}: {appliedDiscount.discountPercent}% off
                      </span>
                      <IonButton fill="clear" size="small" onClick={removeCoupon} style={{ "--color": "var(--ion-color-success-contrast)" }}>
                        <IonIcon slot="icon-only" icon={closeOutline} />
                      </IonButton>
                    </div>
                  )}
                </div>
              )}

              {!covered && calculateNumStubs > 0 && (
                <div style={{ marginTop: 12, paddingTop: 12, textAlign: "center" }}>
                  {appliedDiscount ? (
                    <>
                      <p style={{ textDecoration: "line-through", color: "var(--ion-color-medium)", fontSize: "0.9rem", margin: "0 0 4px" }}>${(stubPricing.price).toFixed(2)}</p>
                      <p style={{ fontWeight: 700, fontSize: "1.3rem", color: "var(--ion-color-success)", margin: "0 0 4px" }}>${appliedDiscount.discountedPrice.toFixed(2)}</p>
                      <p style={{ fontSize: "0.75rem", color: "var(--ion-color-success)", margin: 0 }}>{appliedDiscount.discountPercent}% discount applied</p>
                    </>
                  ) : (
                    <>
                      <p style={{ fontWeight: 700, fontSize: "1.2rem", color: "var(--ion-color-success)", margin: "0 0 4px" }}>${(stubPricing.price).toFixed(2)}</p>
                      <p style={{ fontSize: "0.75rem", color: "var(--ion-color-success)", margin: 0 }}>$9.99 per stub</p>
                    </>
                  )}
                </div>
              )}

              {covered && (
                <div style={{ marginTop: 12, padding: 8, background: "rgba(var(--ion-color-success-rgb),0.15)", borderRadius: 8, textAlign: "center" }}>
                  <p style={{ color: "var(--ion-color-success-shade)", fontWeight: 600, fontSize: "0.875rem", margin: 0 }}>
                    {walletCovers
                      ? `Account Balance ($${walletBalance(user).toFixed(2)})`
                      : `${planIsLive(user) ? "Subscription Active" : "Account Downloads"} (${subRemaining === -1 ? "Unlimited" : `${Math.max(0, subRemaining)} Download${subRemaining === 1 ? "" : "s"} left`})`}
                  </p>
                </div>
              )}

              <IonButton
                expand="block"
                color="success"
                style={{ marginTop: 20, "--border-radius": "4px" }}
                disabled={isProcessing || calculateNumStubs === 0}
                onClick={handleGenerate}
              >
                {isProcessing ? (
                  <IonSpinner name="crescent" style={{ marginRight: 8 }} />
                ) : (
                  <IonIcon slot="start" icon={cloudDownloadOutline} />
                )}
                {isProcessing
                  ? "Processing..."
                  : covered
                    ? `Download ${calculateNumStubs > 0 ? calculateNumStubs : ""} Stub${calculateNumStubs !== 1 ? "s" : ""}`
                    : calculateNumStubs > 0
                      ? `Pay & Download ($${appliedDiscount ? appliedDiscount.discountedPrice.toFixed(2) : (stubPricing.price).toFixed(2)})`
                      : "Configure Pay Period First"
                }
              </IonButton>
              {/* Bundle pricing nudge — "add N more to get 3 for $19.99" / "bundle applied" */}
              {!covered && stubPricing.nudge && (
                <p style={{ margin: "8px 0 0", textAlign: "center", fontSize: "0.78rem", color: stubPricing.applied ? "var(--ion-color-success-shade)" : "var(--ion-color-medium)" }}>
                  {stubPricing.nudge}
                </p>
              )}
            </div>
          </div>
        </div>,
        document.querySelector("ion-app") || document.body
      )}

      <FreshOverlay as={IonAlert}
        isOpen={overflowAsk}
        onDidDismiss={() => setOverflowAsk(false)}
        header="Not enough downloads left"
        message={subRemaining > 0
          ? `Your plan has ${subRemaining} download${subRemaining === 1 ? "" : "s"} left and this order is ${calculateNumStubs} stubs.`
          : `Your plan has no downloads left this period and this order is ${calculateNumStubs} stub${calculateNumStubs === 1 ? "" : "s"}.`}
        buttons={[
          ...(subRemaining > 0 ? [{
            text: `Use ${subRemaining} + pay $${overflowPricing.price.toFixed(2)} for ${overflowCount}`,
            handler: () => openCardCheckout({ subUse: subRemaining, paidCount: overflowCount, price: overflowPricing.price }),
          }] : []),
          { text: `Buy all ${calculateNumStubs} for $${stubPricing.price.toFixed(2)}`, handler: () => openCardCheckout() },
          { text: "Cancel", role: "cancel" },
        ]}
      />

      {pendingCheckout && (
        <CheckoutWithPaywall
          docLabel={pendingCheckout.split
            ? `UK Payslips × ${pendingCheckout.split.paidCount} (${pendingCheckout.split.subUse} covered by your plan)`
            : `UK Payslip${calculateNumStubs !== 1 ? `s × ${calculateNumStubs}` : ""}`}
          documentType="uk-payslip"
          template={selectedTemplate}
          basePrice={pendingCheckout.split ? pendingCheckout.split.price : stubPricing.price}
          discount={pendingCheckout.split ? null : appliedDiscount}
          quantity={pendingCheckout.split ? pendingCheckout.split.paidCount : calculateNumStubs}
          intentExtras={pendingCheckout.split ? { subCovered: pendingCheckout.split.subUse, totalQuantity: calculateNumStubs } : null}
          previewImage={pdfPreviews[0] || null}
          onSuccess={handlePaymentSuccess}
          onClose={() => setPendingCheckout(null)}
        />
      )}

      <FreshOverlay as={IonToast}
        buttons={TOAST_CLOSE_BUTTONS}
        isOpen={toastState.isOpen}
        message={toastState.message}
        color={toastState.color}
        duration={3000}
        onDidDismiss={() => setToastState(s => ({ ...s, isOpen: false }))}
        position="top"
      />
    </Wrap>
  );
}
